import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Animated, ScrollView, StatusBar, useWindowDimensions, View } from 'react-native'
import Constants from 'expo-constants'
import { Footer, Navbar } from './components/Layout'
import { ThemeProvider, useThemeValue } from './ThemeContext'
import { FeaturesPage } from './pages/FeaturesPage'
import { HomePage } from './pages/HomePage'
import { LegalPage } from './pages/LegalPage'
import { PricingPage } from './pages/PricingPage'
import { VisionPage } from './pages/VisionPage'
import { privacySections, termsSections } from './content'
import { HEADER_HEIGHT, IS_WEB, RouteState, ScrollAnchorContext, ScrollStateContext, sharedStyles } from './utils/ui'

const WEBSITE = Constants.expoConfig?.extra?.EXPO_PUBLIC_WEBSITE as string | undefined
const RELAYABLY_APP_URL = Constants.expoConfig?.extra?.EXPO_PUBLIC_RELAYABLY_APP_URL as string | undefined

function parseRoute(input?: string): RouteState {
  if (!IS_WEB || typeof window === 'undefined') {
    return { path: '/' }
  }

  const url = input ? new URL(input, WEBSITE) : window.location
  const pathname = url.pathname as RouteState['path']
  const hashValue = url.hash
  const hash = hashValue ? hashValue.replace(/^#/, '') : undefined
  const allowed: RouteState['path'][] = ['/', '/features', '/pricing', '/vision', '/privacy', '/terms']

  return {
    path: allowed.includes(pathname) ? pathname : '/',
    hash,
  }
}

function RelayablyContent() {
  const { theme } = useThemeValue()
  const { height } = useWindowDimensions()
  const [route, setRoute] = useState<RouteState>(() => parseRoute())
  const scrollRef = useRef<ScrollView | null>(null)
  const anchors = useRef<Record<string, number>>({})
  const pageOpacity = useRef(new Animated.Value(1)).current
  const pageTranslate = useRef(new Animated.Value(0)).current
  const scrollY = useRef(new Animated.Value(0)).current
  const contentRef = useRef<View | null>(null)

  if (!WEBSITE || !RELAYABLY_APP_URL) {
    throw new Error('Relayably website URLs are missing from the environment configuration.')
  }

  const registerAnchor = useCallback((id: string, y: number) => {
    anchors.current[id] = y
  }, [])

  useEffect(() => {
    if (!IS_WEB || typeof window === 'undefined') {
      return
    }
    const listener = () => setRoute(parseRoute())
    window.addEventListener('popstate', listener)
    return () => window.removeEventListener('popstate', listener)
  }, [])

  useEffect(() => {
    pageOpacity.setValue(0)
    pageTranslate.setValue(12)
    Animated.parallel([
      Animated.timing(pageOpacity, { toValue: 1, duration: 260, useNativeDriver: true }),
      Animated.timing(pageTranslate, { toValue: 0, duration: 260, useNativeDriver: true }),
    ]).start()

    const timer = setTimeout(() => {
      const anchorY = route.hash ? anchors.current[route.hash] : undefined
      scrollRef.current?.scrollTo({
        y: anchorY ? Math.max(anchorY - HEADER_HEIGHT - 24, 0) : 0,
        animated: true,
      })
    }, 120)

    return () => clearTimeout(timer)
  }, [pageOpacity, pageTranslate, route.hash, route.path])

  const navigate = useCallback((value: string) => {
    const next = parseRoute(value)
    if (IS_WEB && typeof window !== 'undefined') {
      const url = new URL(value, WEBSITE)
      window.history.pushState({}, '', `${url.pathname}${url.hash}`)
    }
    setRoute(next)
  }, [])

  const openApp = useCallback((plan?: string) => {
    const url = new URL(RELAYABLY_APP_URL)

    if (plan) {
      url.searchParams.set('plan', plan)
    }

    if (typeof window !== 'undefined') {
      window.location.assign(url.toString())
    }
  }, [])

  const renderPage = () => {
    switch (route.path) {
      case '/features':
        return <FeaturesPage />
      case '/pricing':
        return <PricingPage onCtaClick={openApp} />
      case '/vision':
        return <VisionPage />
      case '/privacy':
        return (
          <LegalPage
            title="Privacy Policy"
            updated="Last Updated: May 14, 2026"
            intro="This Privacy Policy explains how we collect, use, and store information when you use the Relayably early access website ('Platform', 'Service', 'we', 'our', or 'us')."
            sections={privacySections}
          />
        )
      case '/terms':
        return (
          <LegalPage
            title="Terms and Conditions"
            updated="Last Updated: May 14, 2026"
            intro='Welcome to the Relayably early access website ("Platform", "Service", "we", "our", or "us"). By accessing or using this website, you agree to these Terms and Conditions. If you do not agree, you should not use the Platform.'
            sections={termsSections}
          />
        )
      case '/':
      default:
        return <HomePage onCtaClick={openApp} />
    }
  }

  return (
    <ScrollAnchorContext.Provider value={{ registerAnchor }}>
      <ScrollStateContext.Provider value={{ scrollY, viewportHeight: height, contentRef }}>
        <View style={{ flex: 1, backgroundColor: theme.colors.background }}>
          <StatusBar barStyle={theme.dark ? 'light-content' : 'dark-content'} />
          <View style={sharedStyles.absoluteFill}>
            <View style={{ position: 'absolute', top: -80, left: -60, width: 260, height: 260, borderRadius: 999, backgroundColor: `${theme.colors.primary}16` }} />
            <View style={{ position: 'absolute', right: -30, top: 280, width: 220, height: 220, borderRadius: 999, backgroundColor: `${theme.colors.tertiary}14` }} />
          </View>
          <Navbar route={route} onNavigate={navigate} onCtaClick={() => openApp()} />
          <ScrollView
            ref={scrollRef}
            style={{ flex: 1 }}
            contentInsetAdjustmentBehavior="never"
            showsVerticalScrollIndicator={IS_WEB}
            onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], { useNativeDriver: false })}
            scrollEventThrottle={16}
          >
            <Animated.View ref={contentRef} style={{ opacity: pageOpacity, transform: [{ translateY: pageTranslate }] }}>
              {renderPage()}
            </Animated.View>
            <Footer onNavigate={navigate} />
          </ScrollView>
        </View>
      </ScrollStateContext.Provider>
    </ScrollAnchorContext.Provider>
  )
}

export default function App() {
  return (
    <ThemeProvider>
      <RelayablyContent />
    </ThemeProvider>
  )
}
